import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  Skeleton,
  Alert,
  Link,
  Tooltip,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import { getPreviousEstimates } from '../../utils/analyticsApi';

const PreviousEstimatesPanel = ({ estimate, sessionId, onViewAll }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (expanded && estimate) {
      fetchPreviousEstimates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, estimate, sessionId]);

  const fetchPreviousEstimates = async () => {
    if (!estimate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getPreviousEstimates(estimate, sessionId, 5);
      setItems(response.data.items || []);
    } catch (err) {
      console.error('Error fetching previous estimates:', err);
      setError('Failed to load previous estimates');
    } finally {
      setLoading(false);
    }
  };

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!estimate) {
    return null;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      sx={{
        mt: 2,
        '&:before': { display: 'none' },
        boxShadow: 1,
        borderRadius: 1,
        '&.Mui-expanded': { margin: '16px 0' }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'grey.50',
          '&:hover': { backgroundColor: 'grey.100' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="action" fontSize="small" />
          <Typography variant="subtitle2">
            Show Previous {estimate}s
          </Typography>
          <Chip
            label={estimate}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 2 }}>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="60%" height={18} />
              </Box>
            ))}
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : items.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No previous items found with estimate "{estimate}"
            </Typography>
          </Box>
        ) : (
          <>
            <List dense sx={{ py: 0 }}>
              {items.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      py: 1.5,
                      px: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Tooltip
                        title={item.hasHighConsensus ? 'High consensus (>80%)' : 'Lower consensus'}
                      >
                        {item.hasHighConsensus ? (
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{ color: 'success.main' }}
                          />
                        ) : (
                          <WarningIcon
                            fontSize="small"
                            sx={{ color: 'warning.main' }}
                          />
                        )}
                      </Tooltip>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 0.5,
                        flexWrap: 'wrap'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {item.sessionName}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {formatDate(item.estimatedAt)}
                      </Typography>
                      {item.consensusRate !== undefined && (
                        <Chip
                          label={`${item.consensusRate}% consensus`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.7rem',
                            bgcolor: item.consensusRate >= 80
                              ? 'success.light'
                              : item.consensusRate >= 60
                              ? 'warning.light'
                              : 'error.light',
                            color: item.consensusRate >= 80
                              ? 'success.dark'
                              : item.consensusRate >= 60
                              ? 'warning.dark'
                              : 'error.dark'
                          }}
                        />
                      )}
                      {item.jiraKey && (
                        <Chip
                          label={item.jiraKey}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>

                    {item.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {item.description}
                      </Typography>
                    )}
                  </ListItem>
                  {index < items.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            <Divider />
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to={`/analytics?estimate=${encodeURIComponent(estimate)}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.875rem'
                }}
              >
                View All in Analytics
                <OpenInNewIcon fontSize="small" sx={{ fontSize: 14 }} />
              </Link>
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default PreviousEstimatesPanel;
